import * as THREE from 'three';

import { SkyState } from './Daylight';

/**
 * The sun, the moon and the sky, as three lights that follow the camera.
 *
 * # Why it follows anything
 *
 * A directional light casts its shadows inside one box, and that box used to be
 * a hundred units wide around the **origin of the world** — the light sat at a
 * fixed point and aimed at (0, 0, 0). A world is thousands of units across, so
 * every player who was not standing near its centre had no shadows at all, and
 * the ones who were had them cut off within half a screen. The box now sits on
 * whatever the camera is looking at and is sized from the frustum.
 *
 * # How big the box is
 *
 * Wide enough for what is on screen, plus the length of the longest shadow that
 * could reach into it. A low sun stretches shadows badly — a hill of twenty
 * levels throws 28 units at 10° above the horizon — so the box grows as the sun
 * comes down, and the map grows with it to keep the shadow sharper than a pixel.
 *
 * The map size steps between fixed sizes rather than following the box exactly:
 * changing it frees and reallocates a depth texture, which is not a thing to do
 * on a frame where the camera merely drifted.
 */

/** Sizes the shadow map may take, smallest first. */
const MAP_SIZES = [1024, 2048, 4096];

/** Shadow texels per world unit to aim for. A hex is 0,87 units across. */
const TEXELS_PER_UNIT = 12;

/** How far up the terrain reaches, in world units: 96 levels at a quarter each. */
const TERRAIN_HEIGHT = 24;

/** Longest shadow the box will stretch for, past which it is simply cut. */
const MAX_STRETCH = 70;

/** How far the light stands from what it lights. Only the direction matters. */
const DISTANCE = 220;

export class SunLight {
  /** The sun by day, the moon by night — one light either way. */
  private readonly key: THREE.DirectionalLight;
  private readonly sky: THREE.HemisphereLight;
  private readonly target = new THREE.Object3D();

  private mapSize = MAP_SIZES[0];

  constructor(scene: THREE.Scene) {
    this.key = new THREE.DirectionalLight(0xffffff, 1);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(this.mapSize, this.mapSize);

    // Along the surface normal rather than into the depth. Every vertex of the
    // terrain is shared between six triangles at a slightly different angle,
    // and a depth bias big enough to cover that lifts shadows off the foot of
    // every slope; a normal bias moves the sample sideways instead.
    this.key.shadow.normalBias = 0.2;
    this.key.shadow.camera.near = 1;
    this.key.shadow.camera.far = DISTANCE * 2;

    // Somewhere sensible until the clock arrives: the light is placed from the
    // sky state every frame, but the first frames are drawn before `welcome`
    // brings one, and a directional light standing on its own target lights
    // nothing at all.
    this.key.position.set(0.5, 1, 0.3).normalize().multiplyScalar(DISTANCE);

    scene.add(this.key);
    scene.add(this.target);
    this.key.target = this.target;

    // Ambient light with a direction to it: the sky above, the ground below.
    // A flat `AmbientLight` washes out exactly what the sun is there to say,
    // and at night it is the only thing left, so it has to carry some shape.
    this.sky = new THREE.HemisphereLight(0xbfd8ff, 0x6b6552, 0.5);
    scene.add(this.sky);
  }

  /**
   * Put the light where the sky says, over the ground the camera is on.
   *
   * `reach` is how far the frustum sees from its focus, in world units — the
   * caller has the camera and the zoom, and this has no business guessing them.
   */
  update(state: SkyState, focus: THREE.Vector3, reach: number): void {
    this.target.position.copy(focus);
    this.key.position.copy(focus).addScaledVector(state.direction, DISTANCE);

    this.key.color.copy(state.sunColor);
    // A grazing light keeps its warmth but loses its edge: the shadow it throws
    // is fading out, so the light that throws it steps back as well and the
    // switch-off at the horizon is not a step.
    this.key.intensity = state.sunIntensity * (0.45 + 0.55 * state.shadowStrength);

    this.sky.color.copy(state.skyColor);
    this.sky.groundColor.copy(state.groundColor);
    this.sky.intensity = state.ambientIntensity;

    // Below the fade the shadow is gone, and with it the acne a horizon-level
    // sun produces however the bias is set.
    this.key.castShadow = state.shadowStrength > 0.02;

    const stretch = Math.min(
      MAX_STRETCH,
      TERRAIN_HEIGHT / Math.max(Math.tan(THREE.MathUtils.degToRad(state.altitude)), 0.15)
    );
    const span = reach * 1.1 + stretch;

    const camera = this.key.shadow.camera;
    camera.left = -span;
    camera.right = span;
    camera.top = span;
    camera.bottom = -span;
    camera.updateProjectionMatrix();

    this.resize(span);
  }

  /** The smallest map that still holds the texel density, at the next step up. */
  private resize(span: number): void {
    const wanted = span * 2 * TEXELS_PER_UNIT;
    const size = MAP_SIZES.find((option) => option >= wanted) ?? MAP_SIZES[MAP_SIZES.length - 1];
    if (size === this.mapSize) return;

    this.mapSize = size;
    this.key.shadow.mapSize.set(size, size);

    // The old depth texture is built for the old size and three.js will not
    // replace it on its own; dropping it is what makes the next frame rebuild.
    this.key.shadow.map?.dispose();
    this.key.shadow.map = null as unknown as THREE.WebGLRenderTarget;
  }

  /** Shadow texels per world unit, for the measurements. */
  get density(): number {
    const camera = this.key.shadow.camera;
    return this.mapSize / (camera.right - camera.left);
  }

  get shadowMapSize(): number {
    return this.mapSize;
  }

  dispose(): void {
    this.key.shadow.map?.dispose();
    this.key.dispose();
    this.sky.dispose();
  }
}
